import { Zap } from 'lucide-react';
import { useLeveragedCefs } from '@/hooks/useLeveragedCefs';
import { BadgeHelpIcon } from '@/components/BadgeKey';

/**
 * Round P136 (Feb 2026) — Small amber ⚡ chip that appears next to
 * the CEF badge when a ticker is in the curated structurally-
 * leveraged set. Silent no-op for every other ticker so callers can
 * drop it in unconditionally right after their CEF badge.
 *
 * Props:
 *   • ticker        — required, used for the O(1) set lookup
 *   • isCef         — required, we only render on CEFs (short-circuit)
 *   • size          — 'sm' (default, matches StockTable badges) | 'md'
 *   • testIdPrefix  — e.g. 'lev-cef-badge' or 'portfolio-lev-cef-badge'
 */
export default function LeveragedCefIcon({
  ticker,
  isCef,
  size = 'sm',
  testIdPrefix = 'lev-cef-badge',
  withHelp = false,
}) {
  const set = useLeveragedCefs();
  if (!isCef) return null;
  if (!ticker) return null;
  if (!set.has(String(ticker).toUpperCase())) return null;

  const pad = size === 'md' ? 'px-1.5 py-0.5' : 'px-1 py-0';
  const iconSize = size === 'md' ? 'h-3.5 w-3.5' : 'h-3 w-3';

  const badge = (
    <span
      className={`inline-flex items-center gap-0.5 ${pad} rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 border border-amber-400/60 dark:border-amber-600/60 cursor-help`}
      title="Structurally leveraged CEF — this fund uses borrowed capital or preferred shares (typically 25-45% effective leverage) to boost distributions. Amplifies both yield and downside risk."
      data-testid={`${testIdPrefix}-${ticker}`}
      aria-label="Structurally leveraged CEF"
    >
      <Zap className={iconSize} />
    </span>
  );
  if (!withHelp) return badge;
  // Sep 2026 — Stock Modal header: ⚡ badge + Badge Key opener (was missing).
  return (
    <span className="inline-flex items-center gap-1">
      {badge}
      <BadgeHelpIcon badgeKey="leveraged_cef" />
    </span>
  );
}
